import { keepPreviousData, useQuery } from "@tanstack/react-query";

import {
  IClinicianForm,
  ICompletedSurveyResponse,
  ISurveyAnswerForEdit,
  IWardSurveyDetail,
  IWardSurveyOverview,
} from "../../../@types/admin/AdminSurvey";
import { ISurveySyncComparison } from "../../../@types/admin/AdminSurveySync";
import { IAuditLog } from "../../../@types/AuditLog";
import { IPaginatedResponse } from "../../../@types/PaginatedResponse";
import { surveysService, surveysSyncService } from "../../../services/admin";
import { ensureOk } from "../../../utils/ensureOk";
import { toAuditPagination } from "./auditLogs";

export const SURVEY_ANSWER_QUERY_KEY = "admin-survey-answer-edit";

/** Clinician forms — the report picker's options. */
export function useClinicianForms() {
  const { data, isLoading, isError } = useQuery<IClinicianForm[]>({
    queryKey: ["admin-clinician-forms"],
    queryFn: async () => ensureOk(await surveysService.getClinicianForms()),
    // No manual Refresh button on migrated admin pages (EN-5129 UI feedback),
    // and the global QueryClient disables refetch-on-mount with a 3-minute
    // staleTime — so re-opening the page must re-read the list.
    refetchOnMount: "always",
  });

  return { clinicianForms: data ?? [], isLoading, isError };
}

/** PROMs per ward. */
export function useWardSurveys() {
  const { data, isLoading, isError } = useQuery<IWardSurveyOverview[]>({
    queryKey: ["admin-ward-surveys"],
    queryFn: async () => ensureOk(await surveysService.getWardSurveys()),
    refetchOnMount: "always",
  });

  return { wardSurveys: data ?? [], isLoading, isError };
}

/** One ward's surveys. Idle until a ward is opened. */
export function useWardSurveyDetails(wardId: number | undefined) {
  const { data, isLoading, isError } = useQuery<IWardSurveyDetail[]>({
    queryKey: ["admin-ward-survey-details", wardId],
    queryFn: async () =>
      ensureOk(await surveysService.getWardSurveyDetails(wardId!)),
    enabled: wardId !== undefined,
    refetchOnMount: "always",
  });

  return { wardSurveyDetails: data ?? [], isLoading, isError };
}

/** One survey's completed responses. Idle until a survey is opened. */
export function useCompletedResponses(surveyId: string | undefined) {
  const { data, isLoading, isError } = useQuery<ICompletedSurveyResponse[]>({
    queryKey: ["admin-completed-responses", surveyId],
    queryFn: async () =>
      ensureOk(await surveysService.getCompletedResponses(surveyId!)),
    enabled: Boolean(surveyId),
    refetchOnMount: "always",
  });

  return { completedResponses: data ?? [], isLoading, isError };
}

/** One response's questions and answers. Idle until the editor opens. */
export function useSurveyAnswerForEdit(surveyAnswerId: string | undefined) {
  const { data, isLoading, isError } = useQuery<ISurveyAnswerForEdit>({
    queryKey: [SURVEY_ANSWER_QUERY_KEY, surveyAnswerId],
    queryFn: async () =>
      ensureOk(await surveysService.getSurveyAnswerForEdit(surveyAnswerId!)),
    enabled: Boolean(surveyAnswerId),
    refetchOnMount: "always",
  });

  return { surveyAnswer: data, isLoading, isError };
}

/**
 * One answer's audit trail. Idle until the history view is opened — Retool
 * gave this query a 40-second timeout, so it is not something to prefetch.
 */
export function useSurveyAnswerAuditLogs(
  surveyAnswerId: string | undefined,
  startDate: string,
  endDate: string,
  page: number,
  pageSize: number
) {
  const { data, isLoading, isError } = useQuery<
    IPaginatedResponse<IAuditLog[]>
  >({
    queryKey: [
      "admin-survey-answer-audit",
      surveyAnswerId,
      startDate,
      endDate,
      page,
      pageSize,
    ],
    queryFn: async () =>
      ensureOk(
        await surveysService.getSurveyAnswerAuditLogs(
          surveyAnswerId!,
          startDate,
          endDate,
          page,
          pageSize
        )
      ),
    enabled: Boolean(surveyAnswerId),
    placeholderData: keepPreviousData,
  });

  return {
    auditLogs: data?.result ?? [],
    pagination: toAuditPagination(data, page, pageSize),
    isLoading,
    isError,
  };
}

export const surveysSyncComparisonKey = ["admin-surveys-sync-comparison"];

/** This region's surveys against Global (EN-5261). */
export function useSurveysSyncComparison() {
  const { data, isLoading, isError } = useQuery<ISurveySyncComparison[]>({
    queryKey: surveysSyncComparisonKey,
    queryFn: async () => ensureOk(await surveysSyncService.getComparison()),
    refetchOnMount: "always",
  });

  return { comparison: data ?? [], isLoading, isError };
}
